/**
 * `validateVendorRedirectUrl` — boundary validation for the vendor-
 * supplied `vendorRedirectUrl` returned by `IIdentityProofingClient.openSession`
 * per CTO security re-scan delta finding NH5 (HIGH).
 *
 * The IAL2 identity-proofing vendor returns a URL the PHG client
 * surfaces as `<a href={vendorRedirectUrl}>` at
 * `<IdentityProofingStep />` for the patient to navigate to the
 * vendor's verification flow. The URL is structurally untrusted
 * — even when the vendor is a contracted IAL2 provider, the URL
 * crosses a substrate-fiduciary trust boundary into the PHG document
 * origin and must be validated before rendering:
 *
 *   - `javascript:` / `data:` / `vbscript:` schemes execute in the
 *     PHG origin when the patient clicks the rendered anchor, with
 *     access to the active patient session memory + Web Crypto API
 *     non-extractable CryptoKey handles + IdentityProofingSession
 *     state — substantively-substantial attacker capability per
 *     `feedback_phg_client_active_session_crypto.md` doctrine.
 *   - Embedded userinfo (`https://user:pass@host/...`) is deprecated
 *     by browsers and a known phishing vector at vendor-redirect
 *     surface.
 *   - Insecure `http:` over the wire would leak the sessionId +
 *     identity-proofing request metadata to passive observers.
 *
 * **Allowed scheme:** `https:` only at v0. Mirrors the
 * `validateHandbackUrl` discipline at the inverse boundary (consent
 * flow terminal redirect to verified app handback URL); both
 * validators preserve the substrate-fiduciary trust boundary
 * canonical at PHG client surface URL handling per Pattern B
 * no-parallel-rendering-surface doctrine.
 *
 * **Native shell wrap (substrate-state-event-paced):** custom-scheme
 * deep-link support (e.g. `phg://`) substrate-state-event-paced for
 * native shell wrap composition at ADR-026/027 unpause cycle. v0
 * web client `https:` only.
 *
 * **Composes with:**
 *   - `validateHandbackUrl` at consent-flow terminal (mirror
 *     discipline at inverse vendor-trust-boundary surface)
 *   - `feedback_phg_client_active_session_crypto.md` doctrine — vendor-
 *     redirect surface NEVER navigates to a URL that could execute in
 *     PHG origin
 *   - `feedback_pattern_b_no_parallel_rendering_surface.md` — vendor
 *     surface renders at vendor origin; PHG remains source-of-truth
 *     for patient consent state
 */
const ALLOWED_VENDOR_REDIRECT_SCHEMES = new Set(['https:']);
export function validateVendorRedirectUrl(raw) {
    let parsed;
    try {
        parsed = new URL(raw);
    }
    catch {
        return {
            ok: false,
            code: 'invalid-format',
            message: 'vendor-redirect URL is not a valid absolute URL',
        };
    }
    if (!ALLOWED_VENDOR_REDIRECT_SCHEMES.has(parsed.protocol)) {
        return {
            ok: false,
            code: 'invalid-scheme',
            message: `vendor-redirect URL scheme "${parsed.protocol}" is not allowed; only https: is permitted at v0`,
        };
    }
    if (parsed.username !== '' || parsed.password !== '') {
        return {
            ok: false,
            code: 'embedded-credentials',
            message: 'vendor-redirect URL must not contain embedded user/password credentials',
        };
    }
    return { ok: true };
}
//# sourceMappingURL=validate-vendor-redirect-url.js.map